// Route: /flashcards/study/[setId] — главный режим обучения. Overlay shell.
//
// Роут — ТОНКИЙ адаптер: весь движок колоды живёт в
// `components/flashcards/study/session-view.tsx` и переиспользуется сценой
// «Карточки» воркспейса курса (декрет E31). Здесь остаётся только роутерное:
// параметры URL, возврат «назад» и переход на виртуальный набор «избранное».
//
// URL state: ?state=complete (force end-of-deck), ?cardIndex=N, ?mode=…,
// ?demo=loading|empty|streaming|end-of-deck|quota-exceeded
// setId — UUID набора либо virtual: 'marked' | 'all' | 'due'.
//
// См. docs/ui-spec/flashcards.study.[setId].{desktop,mobile}.md + flashcards.study.complete.*.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import type { StudyMode } from "@/components/flashcards/study/session-settings-sheet";
import { FlashcardSessionView } from "@/components/flashcards/study/session-view";
import { FLASHCARD_STUDY_DEMOS } from "@/components/flashcards/study/study-demo";
import { useBackFrom } from "@/hooks/use-back-from";
import { fromSearchSchema } from "@/lib/back-from";

/**
 * Параметры режима обучения. Раньше схема была только ТИПОМ (`validateSearch`
 * у роута не было, значения читались кастом как есть): `?cardIndex=abc`
 * доезжал строкой под типом `number`, `?mode=xyz` — неизвестным режимом.
 * Теперь разбор поле за полем (`tolerantSearch`): плохое значение
 * отбрасывается, страница открывается. Контракт теста (`tests/take/$id`) другой
 * и с этой схемой не сливается.
 */
const searchSchema = z
	.object({
		state: z.enum(["complete"]).optional(),
		cardIndex: z.coerce.number().int().nonnegative().optional(),
		mode: z.enum(["srs", "standard", "cram", "marked", "leitner-session"]).optional(),
		demo: z.enum(FLASHCARD_STUDY_DEMOS).optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/flashcards/study/$setId")({
	validateSearch: tolerantSearch(searchSchema),
	component: StudyModePage,
	pendingComponent: StudyPagePending,
});

// Геометрия — та же, что у реальной оболочки (`StudyShell`): `min-h-fit-overlay`,
// а не «ровно экран». Иначе при активном баннере скелетон выше сессии и на
// подмене прыгает layout.
function StudyPagePending(): React.ReactElement {
	return (
		<div className="flex min-h-fit-overlay flex-col bg-background">
			<div className="flex h-[calc(3.5rem+env(safe-area-inset-top,0px))] items-center gap-3 border-b border-border px-4 pt-[env(safe-area-inset-top,0px)] sm:px-6">
				<Skeleton className="size-9 rounded-[10px]" />
				<Skeleton className="h-4 w-44" />
			</div>
			<div className="mx-auto w-full max-w-[560px] px-3 pt-5 sm:px-0">
				<Skeleton className="h-2 w-full rounded-full" />
			</div>
			<div className="flex flex-1 items-center justify-center px-3">
				<Skeleton className="h-[440px] w-full max-w-[560px] rounded-[var(--radius-panel)]" />
			</div>
			<div className="mx-auto w-full max-w-[560px] px-3 pb-9 sm:px-0">
				<Skeleton className="h-[58px] w-full rounded-[13px]" />
			</div>
		</div>
	);
}

function StudyModePage(): React.ReactElement {
	const { setId } = useParams({ from: "/flashcards/study/$setId" });
	const search = useSearch({ from: "/flashcards/study/$setId" });
	const navigate = useNavigate({ from: "/flashcards/study/$setId" });
	const goBack = useBackFrom(search.from, () => {
		void navigate({ to: "/flashcards" });
	});

	return (
		<FlashcardSessionView
			setId={setId}
			initialCardIndex={search.cardIndex}
			initialMode={search.mode as StudyMode | undefined}
			forceComplete={search.state === "complete" || search.demo === "end-of-deck"}
			demo={search.demo}
			onExit={goBack}
			onContinueMarked={() =>
				void navigate({
					to: "/flashcards/study/$setId",
					params: { setId: "marked" },
				})
			}
		/>
	);
}
